import React, { useState, useEffect } from "react";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";
import axios from "axios";
import { baseurl } from "../../baseurl";
import { FaEye, FaFile } from "react-icons/fa";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";

function OrderStatus() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    fromDate: "",
    toDate: "",
  });

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [totalCount, setTotalCount] = useState(0);

  // ✅ Fetch orders from backend
  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit,
        status: filters.status || undefined,
        search: filters.search || undefined,
        start_date: filters.fromDate
          ? filters.fromDate.toISOString().split("T")[0]
          : undefined,
        end_date: filters.toDate
          ? filters.toDate.toISOString().split("T")[0]
          : undefined,
      };

      const res = await axios.get(`${baseurl}getAllOrdersForAdmin`, { params });
      if (res.data.success) {
        setOrders(res.data.orders || []);
        setTotalCount(res.data.count || 0);
      } else {
        Swal.fire("Error", "Failed to fetch orders", "error");
      }
    } catch (err) {
      console.error("Fetch orders error:", err);
      Swal.fire("Error", "Failed to fetch orders", "error");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Refetch when filters, page, or limit change
  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page, limit]);

  // ✅ Debounce search input
  useEffect(() => {
    const delay = setTimeout(() => {
      setPage(1);
      fetchOrders();
    }, 500);

    return () => clearTimeout(delay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search]);

  return (
    <>
      <Topbar />
      <Sidebar />

      <main className="main-wrapper">
        <div className="main-content">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4>All Invoices |</h4>
          </div>

          {/* 🔹 Filters */}
          <div className="card p-2 mb-3">
            <h6>Filters</h6>
            <div className="row g-2">
              <div className="col-md-4">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by Customer, Email, Phone, Vendor, UUID"
                  value={filters.search}
                  onChange={(e) =>
                    setFilters({ ...filters, search: e.target.value })
                  }
                />
              </div>
              <div className="col-md-2">
                <select
                  className="form-select"
                  value={filters.status}
                  onChange={(e) =>
                    setFilters({ ...filters, status: e.target.value })
                  }
                >
                  <option value="">All Status</option>
                  <option value="Pending">Pending</option>
                  <option value="Confirmed">Confirmed</option>
                  <option value="Shipped">Shipped</option>
                  <option value="Delivered">Delivered</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </div>
              <div className="col-md-2">
                <Flatpickr
                  options={{ dateFormat: "Y-m-d" }}
                  placeholder="From Date"
                  className="form-control"
                  value={filters.fromDate}
                  onChange={(date) =>
                    setFilters({ ...filters, fromDate: date[0] })
                  }
                />
              </div>
              <div className="col-md-2">
                <Flatpickr
                  options={{ dateFormat: "Y-m-d" }}
                  placeholder="To Date"
                  className="form-control"
                  value={filters.toDate}
                  onChange={(date) =>
                    setFilters({ ...filters, toDate: date[0] })
                  }
                />
              </div>
            </div>
          </div>

          {/* 🔹 Table */}
          <div className="card shadow-sm border-0">
            <div className="card-body table-responsive">
              <table className="table table-hover table-striped align-middle">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Order UUID</th> 
                    <th>Vendors</th>
                    <th>Amount</th>
                    <th>Order Status</th>
                    <th>Payment</th>
                    <th>Method</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="10" className="text-center py-4">
                        Loading...
                      </td>
                    </tr>
                  ) : orders.length > 0 ? (
                    orders.map((o, i) => (
                      <tr key={o.order_id}>
                        <td>{(page - 1) * limit + i + 1}</td>
                       <td className="text-break">
  {o.order_uuid}
</td> 
                        <td>
                          {o.vendors?.map((v, idx) => (
                            <div key={idx}>
                              <small>
                                {v.vendor_name} ({v.business_name})
                              </small>
                            </div>
                          ))}
                        </td>
                        <td>₹{o.total_amount?.toLocaleString()}</td>
                        <td>
                          <span
                            className={`badge ${
                              o.order_status === "Delivered"
                                ? "bg-success"
                                : o.order_status === "Cancelled"
                                ? "bg-danger"
                                : o.order_status === "Shipped"
                                ? "bg-info"
                                : "bg-warning"
                            }`}
                          >
                            {o.order_status}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`badge ${
                              o.payment_status === "Paid" ? "bg-success" : "bg-danger"
                            }`}
                          >
                            {o.payment_status}
                          </span>
                        </td>
                        <td>
                          <span className="badge bg-secondary">
                            {o.payment_method?.toUpperCase()}
                          </span>
                        </td>
                        <td>
                          {new Date(o.created_at).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })}
                        </td>
                        <td>
                          <Link
                            to={`/order-view/${o.order_uuid}`}
                            className="btn btn-sm btn-outline-primary"
                            title="View Order"
                          >
                            <FaFile />
                          </Link>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="10" className="text-center py-4 text-muted">
                        No orders found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {/* Pagination */}
              <div className="d-flex justify-content-between align-items-center mt-3">
                <div>
                  Showing {(page - 1) * limit + 1} -{" "}
                  {Math.min(page * limit, totalCount)} of {totalCount}
                </div>
                <div>
                  <button
                    className="btn btn-sm btn-outline-primary me-2"
                    disabled={page <= 1}
                    onClick={() => setPage(page - 1)}
                  >
                    Previous
                  </button>
                  <button
                    className="btn btn-sm btn-outline-primary"
                    disabled={page >= Math.ceil(totalCount / limit)}
                    onClick={() => setPage(page + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}

export default OrderStatus;
